// In-world NPC chibi, the small paper-doll sprite the GX/DM cast walk around as,
// exactly like the player's ChibiAvatar but driven by a fixed character "preset"
// (a01..d10) instead of the customization slots. The original game ships these as
// pre-baked `cn_<preset>_bo_<dir>` body sprites (one 128×128 chibi per direction)
// plus a separate `cn_<preset>_ey_<dir>` face overlay, the body's face is BLANK,
// so the eyes layer is required to give the character a face. We stack body + eyes
// for the current facing and mirror the whole figure for the right-hand trio,
// matching ChibiAvatar's feet-anchor + direction mapping so NPCs sit and Y-sort
// identically to players. The back view (dir 5) has no face overlay.

import { Container, Sprite, Texture, Assets } from "pixi.js";
import type { ChibiManifest } from "@ygoevo/yo2-data";
import type { Facing } from "@ygoevo/world-protocol";
import { asset } from "../data.ts";

const SIZE = 128;
// Layer offsets are the SWF ground truth from the cn_n0<dir>.txt placement tables
// (PlaceObject2 TranslateX/Y in twips ÷20 → px): shadow (22,104), body (-18,2),
// eyes (14,4), constant across all presets and directions. Expressed relative to
// the body (which we draw at 0,0): eyes = body+(32,2), shadow = body+(40,102).
const EYE_OFFSET = { x: 32, y: 2 };
const SHADOW = { path: "characters/npc/cn_sh_01.png", x: 40, y: 102 };

// Our 8-way facing → source direction index + horizontal mirror (identical to
// ChibiAvatar: 01 S, 02 SW, 03 W, 04 NW, 05 N; the right trio SE/E/NE mirrors the
// left). Dir 05 (back) has no eyes overlay; the diagonals (02/04) do.
const FACING_DIR: Record<Facing, { dir: number; mirror: boolean }> = {
  s: { dir: 1, mirror: false },
  sw: { dir: 2, mirror: false },
  w: { dir: 3, mirror: false },
  nw: { dir: 4, mirror: false },
  n: { dir: 5, mirror: false },
  ne: { dir: 4, mirror: true },
  e: { dir: 3, mirror: true },
  se: { dir: 2, mirror: true },
};

export interface NpcChibiOptions {
  manifest: ChibiManifest;
  /** Character preset id (a01..d10), must exist in manifest.npc.presets. */
  preset: string;
  /** On-screen height in px for the 128-tall sprite. */
  displayHeight: number;
  facing?: Facing;
}

export class NpcChibi {
  readonly root = new Container();
  private body = new Container();
  private opts: NpcChibiOptions;
  private facing: Facing;
  private rebuildGen = 0;

  constructor(opts: NpcChibiOptions) {
    this.opts = opts;
    this.facing = opts.facing ?? "s";
    this.body.pivot.set(64, 112); // feet-centre, matches the player chibi
    this.root.addChild(this.body);
    this.root.scale.set(opts.displayHeight / SIZE);
  }

  /** Resolve a slot sprite path for a direction via the manifest's npc template. */
  private path(slot: "bo" | "ey", dir: number): string {
    const tmpl = this.opts.manifest.fileTemplate.npc; // cn_{preset}_{slot}_{pose}{dir}_{frame}.png
    return tmpl
      .replace("{preset}", this.opts.preset)
      .replace("{slot}", slot)
      .replace("{pose}", "n")
      .replace("{dir}", String(dir).padStart(2, "0"))
      .replace("{frame}", "01");
  }

  async build(): Promise<void> {
    await this.rebuild();
  }

  /** Swap to the current facing: body + eyes (no eyes on the back view). Old
   *  sprites stay until the new textures have loaded, then swap in one frame. */
  private async rebuild(): Promise<void> {
    const gen = ++this.rebuildGen;
    const { dir, mirror } = FACING_DIR[this.facing];

    // Ground shadow (shared across all NPCs), drawn behind the body so the chibi
    // sits on the floor instead of floating.
    const shadow = new Sprite();
    shadow.position.set(SHADOW.x, SHADOW.y);
    const loads: Promise<void>[] = [
      Assets.load(asset(SHADOW.path))
        .then((t: Texture) => {
          shadow.texture = t;
        })
        .catch(() => {
          shadow.visible = false;
        }),
    ];

    const body = new Sprite();
    loads.push(
      Assets.load(asset(this.path("bo", dir)))
        .then((t: Texture) => {
          body.texture = t;
        })
        .catch(() => {
          body.visible = false;
        }),
    );

    // Eyes overlay, present for the front/3-4/side views (dirs 1-4), absent for
    // the back (dir 5). Without it the face is blank.
    let eyes: Sprite | null = null;
    if (dir !== 5) {
      eyes = new Sprite();
      eyes.position.set(EYE_OFFSET.x, EYE_OFFSET.y);
      loads.push(
        Assets.load(asset(this.path("ey", dir)))
          .then((t: Texture) => {
            eyes!.texture = t;
          })
          .catch(() => {
            eyes!.visible = false;
          }),
      );
    }

    await Promise.all(loads);
    if (gen !== this.rebuildGen) {
      shadow.destroy();
      body.destroy();
      eyes?.destroy();
      return;
    }
    const old = this.body.removeChildren();
    this.body.scale.x = mirror ? -1 : 1;
    this.body.addChild(shadow, body);
    if (eyes) this.body.addChild(eyes);
    for (const c of old) c.destroy();
  }

  setFacing(f: Facing): void {
    if (f === this.facing) return;
    this.facing = f;
    void this.rebuild();
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}
